import { LanguageService } from './language.service';
import { Logger, LoggerFactory } from './logger.service';
import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { SnackComponent } from 'app/shared/snack/snack.component';

export enum SnackBarPosition {
  BottomCenter,
  BottomLeft,
  BottomRight,
}

@Injectable()
export class SnackBarService {
  private readonly logger: Logger;
  private readonly errorClasses = ['snack-bar', 'snack-bar--error'];
  private readonly infoClasses = ['snack-bar', 'snack-bar--info'];
  private readonly successClasses = ['snack-bar', 'snack-bar--success'];
  private readonly warningClasses = ['snack-bar', 'snack-bar--warning'];
  private snackBarRef: MatSnackBarRef<any> | null = null;

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly translateService: LanguageService,
    public readonly loggerFactory: LoggerFactory,
  ) {
    this.logger = loggerFactory.get('SnackBarService');
  }

  // Shows default error message or message for the given translation key.
  public async error(
    translationKey = 'generic.error',
    subTranslationKey = '',
    duration = 10000,
    position = SnackBarPosition.BottomCenter,
  ) {
    const messageText = await this.translateService.translateAsync(translationKey);
    const subMessageText = subTranslationKey ? await this.translateService.translateAsync(subTranslationKey) : '';

    this.snackBarRef = this.snackBar.openFromComponent(
      SnackComponent,
      this.getSnackBarConfig(duration, position, this.errorClasses, {
        messageText,
        subMessageText,
      }),
    );
  }

  // Shows default success message or message for the given translation key.
  public async success(
    translationKey = 'generic.success',
    subTranslationKey = '',
    duration = 10000,
    position = SnackBarPosition.BottomCenter,
    action?,
  ) {
    const messageText = await this.translateService.translateAsync(translationKey);
    const subMessageText = subTranslationKey ? await this.translateService.translateAsync(subTranslationKey) : '';

    this.snackBarRef = this.snackBar.openFromComponent(
      SnackComponent,
      this.getSnackBarConfig(duration, position, this.successClasses, {
        messageText,
        subMessageText,
        action,
      }),
    );
  }

  // Shows warning message for the given translation key.
  public async warning(
    translationKey: string,
    subTranslationKey = '',
    duration = 10000,
    position = SnackBarPosition.BottomCenter,
    parameters?: any,
  ) {
    if (!translationKey) {
      this.logger.error('Warning translation key is empty');
    }
    let messageText = await this.translateService.translateAsync(translationKey);
    const subMessageText = subTranslationKey ? await this.translateService.translateAsync(subTranslationKey) : '';
    if (parameters) {
      for (const key in parameters) {
        messageText = messageText.replace(key, parameters[key]);
      }
    }

    this.snackBarRef = this.snackBar.openFromComponent(
      SnackComponent,
      this.getSnackBarConfig(duration, position, this.warningClasses, {
        messageText,
        subMessageText,
      }),
    );
  }

  // Shows information message for the given translation key.
  public async info(
    translationKey: string,
    subTranslationKey = '',
    duration = 10000,
    position = SnackBarPosition.BottomCenter,
  ) {
    if (!translationKey) {
      this.logger.error('Info translation key is empty');
    }
    const messageText = await this.translateService.translateAsync(translationKey);
    const subMessageText = subTranslationKey ? await this.translateService.translateAsync(subTranslationKey) : '';
    this.snackBarRef = this.snackBar.openFromComponent(
      SnackComponent,
      this.getSnackBarConfig(duration, position, this.infoClasses, {
        messageText,
        subMessageText,
      }),
    );
  }

  private getSnackBarConfig(
    duration = 10000,
    snackBarPosition: SnackBarPosition,
    panelClass: Array<string>,
    properties?,
  ) {
    const config = this.getSnackBarPosition(snackBarPosition) as any;
    config.duration = duration;
    config.panelClass = panelClass;
    config.data = properties;
    return config;
  }

  private getSnackBarPosition(snackBarPosition: SnackBarPosition) {
    switch (snackBarPosition) {
      case SnackBarPosition.BottomLeft:
        return { horizontalPosition: 'left', verticalPosition: 'bottom' };
      case SnackBarPosition.BottomCenter:
        return { horizontalPosition: 'center', verticalPosition: 'bottom' };
      case SnackBarPosition.BottomRight:
        return { horizontalPosition: 'right', verticalPosition: 'bottom' };
      default:
        return { horizontalPosition: 'center', verticalPosition: 'bottom' };
    }
  }

  public closeSnackBar() {
    if (this.snackBarRef) {
      this.snackBarRef.dismiss();
      this.snackBarRef = null;
    }
  }
}
